import { Alert, Button, Card, Checkbox, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRpc } from '../modules/risq/data';
import { PageTitle, usePositionNames } from '../ui/components';
import { useWorkplace } from './access';

/** Positions every company uses: the access rules depend on them (`set_company_setting`). */
export const ALWAYS_USED = ['MASTER', 'CE'];

/** The server's refusal when somebody holds the position (`set_company_setting`, 23514). */
const POSITION_HELD = 'holds this position';

/**
 * Company settings (DPA, Admin; D132): which positions the company uses on its vessels. Unused
 * positions stay in the system but leave every list of positions in the dashboard and on the
 * phones (after their next sync).
 */
export function CompanySettingsPage() {
  const { t } = useTranslation('dashboard');
  const { access, company } = useWorkplace();
  const { name } = usePositionNames();
  const stored = access.unusedPositions[company.id] ?? [];
  const [unused, setUnused] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useRpc('set_company_setting', [['access']]);
  const draft = unused ?? stored;
  const changed = [...draft].sort().join() !== [...stored].sort().join();

  return (
    <>
      <PageTitle title={t('companySettings.title', { company: company.name })} />
      <Card withBorder maw={640}>
        <Stack>
          <Title order={2} size="h3">
            {t('companySettings.positionsTitle')}
          </Title>
          <Text>{t('companySettings.positionsBody')}</Text>
          {access.positions.map((p) => {
            const always = ALWAYS_USED.includes(p.code);
            return (
              <Checkbox
                key={p.code}
                label={name(p.code)}
                description={always ? t('companySettings.alwaysUsed') : undefined}
                checked={!draft.includes(p.code)}
                disabled={always}
                onChange={(event) => {
                  setSaved(false);
                  setUnused(
                    event.currentTarget.checked
                      ? draft.filter((c) => c !== p.code)
                      : [...draft, p.code],
                  );
                }}
              />
            );
          })}
          {save.error === null ? null : (
            <Alert color="red" role="alert">
              {save.error.message.includes(POSITION_HELD)
                ? t('companySettings.inUse')
                : t('companySettings.failed')}
            </Alert>
          )}
          {saved ? (
            <Alert color="green" role="status">
              {t('companySettings.saved')}
            </Alert>
          ) : null}
          <Button
            w="fit-content"
            disabled={!changed}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  p_company_id: company.id,
                  p_key: 'positions.not_used',
                  p_value: draft,
                },
                {
                  onSuccess: () => {
                    setUnused(null);
                    setSaved(true);
                  },
                },
              )
            }
          >
            {t('companySettings.save')}
          </Button>
        </Stack>
      </Card>
    </>
  );
}
